import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useAddOnRetireMutation } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.retire.mutation"
import { evictAddOnCatalogQueries } from "@/v2-backoffice-client/license/license.utils"
import type { AddOnCatalogFragment } from "@/v2-backoffice-client/license/addOns/catalog/__types__/license.addOn.catalog.fragment"

export type LicenseAddOnRetireMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  addOn: AddOnCatalogFragment
}

// Retires an add-on from the catalog. Deliberately NOT a mass-revoke:
// existing grants stay on their licenses until an operator revokes them —
// the confirm copy spells that out when grants exist, so nobody expects
// retiring to switch customers off.
function LicenseAddOnRetireMenuItem(props: LicenseAddOnRetireMenuItemProps) {
  const { addOn, ...menuItemProps } = props

  const [retireAddOn, { loading, error }] = useAddOnRetireMutation({
    update: evictAddOnCatalogQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Add-on retired",
        description: `${addOn.Name} is gone from the catalog and the grant picker.`,
      })
    },
  })

  return (
    <ActionMenu.ConfirmItem
      iconSrc="trash"
      variant="destructive"
      isLoading={loading}
      error={error}
      errorTitle="Failed to retire add-on"
      confirmTitle="Retire Add-on"
      confirmDescription={
        addOn.GrantCount > 0
          ? `${addOn.Name} disappears from the catalog and can't be granted again — but the ${addOn.GrantCount} existing grant(s) stay on their licenses until revoked. Retiring is never a silent mass-revoke.`
          : `${addOn.Name} disappears from the catalog and can't be granted again. Nothing holds it today, so no customer is affected.`
      }
      confirmText="Retire"
      confirmVariant="destructive"
      data-feature-code="UI_BackofficeAddOn_Row_Retire"
      onConfirm={async () => {
        await retireAddOn({ variables: { id: addOn.Id } })
      }}
      {...menuItemProps}
    >
      Retire
    </ActionMenu.ConfirmItem>
  )
}

export default LicenseAddOnRetireMenuItem
